/*
 * No.9 Caffè — Article page ("In this article" navigation)
 *
 * An article's body is a single rich-text blob written in the Shopify editor.
 * Liquid cannot walk it, so there is no way to know what headings a post
 * contains until it is rendered. This builds the nav from the DOM instead:
 *
 *   1. collect the headings inside .ar9__content
 *   2. give each one a stable id (keeping any the author already set)
 *   3. build the list, and reveal the rail only if there was something to list
 *   4. highlight the section being read, and scroll smoothly when one is picked
 *
 * If a post has no headings, or this file never loads, the rail stays hidden
 * and the article renders full width. The post is always readable either way.
 */
(function () {
  'use strict';

  function slugify(text, used) {
    var base = String(text)
      // c-caron, s-caron and z-caron decompose to c/s/z, so Slovenian
      // headings produce clean ASCII anchors instead of percent-escapes.
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    if (!base) base = 'section';

    var id = base;
    var n = 2;
    while (used[id] || document.getElementById(id)) {
      id = base + '-' + n;
      n++;
    }
    used[id] = true;
    return id;
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function init(root) {
    var content = root.querySelector('.ar9__content');
    var toc = root.querySelector('.ar9__toc');
    var list = root.querySelector('.ar9__toc-list');
    var body = root.querySelector('.ar9__body');
    if (!content || !toc || !list || !body) return;

    var selector = toc.getAttribute('data-headings') || 'h2';
    var headings = Array.prototype.slice.call(content.querySelectorAll(selector));

    // Headings with no text (a stray empty tag from the editor) would produce
    // a blank nav row, so they are dropped rather than listed.
    headings = headings.filter(function (h) {
      return h.textContent && h.textContent.trim().length > 0;
    });

    // Nothing to list: leave the layout exactly as the CSS ships it, full
    // width with the rail hidden.
    if (!headings.length) return;

    var used = {};
    var links = [];

    headings.forEach(function (h) {
      if (h.id) {
        used[h.id] = true;
      } else {
        h.id = slugify(h.textContent.trim(), used);
      }

      var li = document.createElement('li');
      li.className = 'ar9__toc-item ar9__toc-item--' + h.tagName.toLowerCase();

      var a = document.createElement('a');
      a.className = 'ar9__toc-link';
      a.href = '#' + h.id;
      a.textContent = h.textContent.trim();
      // The full heading is available on hover, since inactive rows are
      // deliberately clipped to one line.
      a.title = h.textContent.trim();

      li.appendChild(a);
      list.appendChild(li);
      links.push({ li: li, a: a, heading: h });
    });

    toc.classList.add('is-ready');
    body.classList.add('has-toc');

    /* ---- Which section am I reading? ----
       The active heading is the last one whose top has passed the offset
       line. A scroll listener is used rather than IntersectionObserver
       because "last one passed" is exactly the question being asked, and an
       observer answers a different one (what is currently intersecting),
       which goes wrong for headings taller or shorter than the viewport. */
    function offsetLine() {
      var n = parseFloat(toc.getAttribute('data-offset'));
      return (n > 0 ? n : 100) + 8;
    }

    var rail = root.querySelector('.ar9__toc-rail');
    var marker = root.querySelector('.ar9__toc-marker');

    var activeIndex = -1;
    var collapsedH = 0;

    // Every collapsed row is one line, so a single measurement describes all of
    // them. That is what lets the marker's destination be COMPUTED rather than
    // read from the DOM mid-animation: when a row above the target collapses,
    // the rows below it are still moving, so a measured position would be the
    // pre-collapse one and the marker would visibly chase the row. i * height
    // is the settled position, so both arrive together.
    function measureCollapsed() {
      for (var i = 0; i < links.length; i++) {
        if (!links[i].li.classList.contains('is-active')) return links[i].a.offsetHeight;
      }
      return links.length ? links[0].a.offsetHeight : 0;
    }

    function durationMs() {
      var v = getComputedStyle(root).getPropertyValue('--ar9-dur').trim();
      var n = parseFloat(v);
      if (!n) return 340;
      return v.indexOf('ms') > -1 ? n : n * 1000;
    }

    // Opening a row: clear the cap, read the wrapped content, put the exact
    // value back. Both happen inside one frame, so nothing paints in the
    // collapsed state and the ease lands on the row's real height rather than
    // rushing toward some arbitrary large max-height.
    //
    // Then, once the ease has finished, the cap is dropped altogether. A
    // measured height that is even a pixel short would otherwise shave the
    // last line off permanently, and the row being READ is the one row that
    // must never hide text. After this the height is the content's own.
    function openRow(a) {
      clearTimeout(a.__no9Timer);
      a.style.maxHeight = '';
      var h = a.scrollHeight;
      a.style.maxHeight = h + 'px';
      a.__no9Timer = setTimeout(function () {
        if (a.parentNode && a.parentNode.classList.contains('is-active')) {
          a.style.maxHeight = 'none';
        }
      }, durationMs() + 40);
      return h;
    }

    // Closing a row: nothing can animate away from `none`, so pin the current
    // height first and let the layout take it before handing back to the CSS
    // one-line cap.
    function closeRow(a) {
      clearTimeout(a.__no9Timer);
      if (a.style.maxHeight === 'none') {
        a.style.maxHeight = a.scrollHeight + 'px';
        void a.offsetHeight;
      }
      a.style.maxHeight = '';
    }

    function moveMarker(i, openH) {
      if (!marker) return;
      marker.style.transform = 'translateY(' + i * collapsedH + 'px)';
      marker.style.height = openH + 'px';
      marker.classList.add('is-visible');
    }

    // Scroll the rail itself, never the window: adjusting scrollTop directly
    // avoids scrollIntoView's habit of moving the page as well.
    function keepInView(i, openH) {
      var totalH = (links.length - 1) * collapsedH + openH;
      if (totalH <= toc.clientHeight) return;
      var top = (rail ? rail.offsetTop : 0) + i * collapsedH;
      var bottom = top + openH;
      if (top < toc.scrollTop) {
        toc.scrollTop = top;
      } else if (bottom > toc.scrollTop + toc.clientHeight) {
        toc.scrollTop = bottom - toc.clientHeight;
      }
    }

    function setActive(i) {
      if (i === activeIndex) return;

      if (activeIndex > -1) {
        links[activeIndex].li.classList.remove('is-active');
        closeRow(links[activeIndex].a);
      }

      activeIndex = i;
      if (i < 0) return;

      links[i].li.classList.add('is-active');
      if (!collapsedH) collapsedH = measureCollapsed();
      var openH = openRow(links[i].a);
      moveMarker(i, openH);
      keepInView(i, openH);
    }

    // Re-measure after anything that changes wrapping: a resize, or the real
    // heading font arriving.
    function refresh() {
      collapsedH = measureCollapsed();
      if (activeIndex > -1) {
        moveMarker(activeIndex, openRow(links[activeIndex].a));
      }
    }

    function update() {
      var line = offsetLine();
      var found = -1;
      for (var i = 0; i < headings.length; i++) {
        if (headings[i].getBoundingClientRect().top <= line) found = i;
        else break;
      }
      // Above the first heading, keep the first row lit rather than none, so
      // the rail never looks broken at the top of the article.
      setActive(found < 0 ? 0 : found);
    }

    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        update();
        ticking = false;
      });
    }

    var resizeTimer = null;
    function onResize() {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        refresh();
        update();
      }, 120);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });

    /* ---- Click a row, glide to the section ---- */
    list.addEventListener('click', function (e) {
      var a = e.target.closest('.ar9__toc-link');
      if (!a) return;
      var id = a.getAttribute('href').slice(1);
      var target = document.getElementById(id);
      if (!target) return;

      e.preventDefault();

      // Respond on the click. Waiting for the smooth scroll to reach the
      // heading leaves the rail lagging behind the tap by a third of a second.
      var idx = links.findIndex(function (l) { return l.heading === target; });
      if (idx > -1) setActive(idx);

      target.scrollIntoView({
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        block: 'start'
      });
      // Update the address bar without the jump a bare hash assignment causes.
      if (window.history && window.history.replaceState) {
        window.history.replaceState(null, '', '#' + id);
      }
    });

    // Landing on a deep link: the browser jumps before our scroll-margin is
    // necessarily applied, so nudge it into place and light the right row.
    if (window.location.hash) {
      var landed = document.getElementById(window.location.hash.slice(1));
      if (landed && content.contains(landed)) {
        window.requestAnimationFrame(function () {
          landed.scrollIntoView({ behavior: 'auto', block: 'start' });
          update();
        });
      }
    }

    collapsedH = measureCollapsed();
    update();

    // N27 loads asynchronously; until it swaps in, rows are measured in the
    // fallback face and wrap differently.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready
        .then(function () {
          refresh();
          update();
        })
        .catch(function () {});
    }
  }

  function boot() {
    document.querySelectorAll('.ar9').forEach(function (root) {
      init(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor re-render.
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector ? e.target.querySelector('.ar9') : null;
    if (root) init(root);
  });
})();
